import { useShallow } from "zustand/shallow";
import { Box, Card, CardContent, CardHeader, Chip, ChipProps, Divider, Typography } from "@mui/material";
import Icon from "@mdi/react";

import AppBar from "components/AppBar";
import AsyncAvatar from "components/AsyncAvatar";
import DataIconPlaceholder from "components/DataIconPlaceholder";
import PageLayout from "components/PageLayout";
import getNameInitials from "utils/getNameInitials";
import getNounCount from "utils/getNounCount";
import { StorageBuckets } from "api/storage";
import useBoundStore from "store";
import { VEHICLES } from "containers/Event/VehicleSelectionDialog/VehicleSelectionDialog";
import { GetUserById } from "api/profile";
import VoteApi, { VoteByEventId } from "api/vote";
import VirtualizedList from "components/VirtualizedList";
import { mdiCarSeat } from "@mdi/js";
import { useMemo } from "react";
import useNotifier from "hooks/useNotifier";
import {
  getAllAvailableSeats,
  getDriverOfCurrentUser,
  getDrivers,
  getIsOwnerOfCar,
  getPassengersCount,
  userIsDriver,
} from "utils/drivers";

export interface VehiclesProps {
  users: GetUserById[];
  /** If true, all seats are disabled. Used to view timeline events details */
  viewMode?: boolean;
  votes: VoteByEventId[];
  onBack?: () => void;
}

function Vehicles(props: VehiclesProps) {
  const { users, viewMode = false, votes, onBack } = props;
  const selectedEventId = useBoundStore((state) => state.selected.eventId);
  const userAuthId = useBoundStore((state) => state.user.auth?.id);
  const { isMobileDevice, smallAvatars } = useBoundStore(
    useShallow((state) => ({
      isMobileDevice: state.miscellaneous.isMobileDevice,
      smallAvatars: state.miscellaneous.smallAvatars,
    }))
  );
  const drivers = getDrivers(votes, users);
  const allAvailableSeats = getAllAvailableSeats(drivers);
  const passengersCount = getPassengersCount(drivers);
  const isDriver = userIsDriver(drivers, userAuthId);
  const driverOfCurrentUser = getDriverOfCurrentUser(drivers, userAuthId);
  const isParticipant = Boolean(votes.find((v) => v.uid === userAuthId && v.type === "participating"));

  const { showNotification } = useNotifier();
  const voteApi = useMemo(() => new VoteApi(showNotification), []);
  // FIXME: if user is passenger in, he can't modify his vote anymore
  const handleSelectEmptySeat = async (driverUid: number, oldDriverUid?: number) => {
    if (!isParticipant) {
      return showNotification({
        message: "To claim a seat, you must join the event.",
        severity: "warning",
      });
    }

    if (selectedEventId) {
      await voteApi.takeVehicleSeat(driverUid, selectedEventId, oldDriverUid);
    }

    return undefined;
  };

  const handleRemoveOwnVehicleSeat = async (driverUid: number) => {
    if (selectedEventId) {
      await voteApi.removeOwnVehicleSeat(driverUid, selectedEventId);
    }
  };

  const handleRemovePassenger = async (passengerId: number) => {
    const driverVote = votes.find((v) => v.uid === userAuthId);
    if (driverVote?.id) {
      await voteApi.updateVote(driverVote.id, {
        ...driverVote,
        passengers: driverVote.passengers.filter((pUid) => pUid !== passengerId),
      });
    }
  };

  const handleDelete = (params: HandleDeleteParams) => {
    const { driverUid, isOwningSeat, isOwnerOfCar, passengerId } = params;

    if (isOwningSeat) {
      return handleRemoveOwnVehicleSeat(driverUid);
    }

    if (isOwnerOfCar) {
      return handleRemovePassenger(passengerId);
    }

    return undefined;
  };

  /**
   * Generate items which will be used to display de virtualized list
   *
   * @returns
   */
  function generateVirtualizedList(): JSX.Element[] {
    const vrList: JSX.Element[] = [];

    drivers.forEach((driver) => {
      const isOwnerOfCar = getIsOwnerOfCar(driver, userAuthId);
      const isPassenger = driver.passengers.includes(userAuthId ?? Infinity);
      const passengers = users.filter((u) => driver.passengers.includes(u.uid));
      const emptySeats = driver.vehicle_details.availableSeats - (driver.passengers?.length ?? 0) - 1;
      const noSeatsAvailable = emptySeats === 0 && !driver.passengers.length;

      vrList.push(
        <Card sx={{ mx: 2, mb: 2 }}>
          <CardHeader
            avatar={
              <AsyncAvatar
                avatarFile={driver.avatar ? smallAvatars[driver.avatar] : undefined}
                avatarName={driver.avatar}
                placeholder={getNameInitials(driver.name, 2)}
                bucket={StorageBuckets.PROFILES_AVATARS}
              />
            }
            action={
              <Box alignItems='center' color='primary.main' display='flex' p={1}>
                <Icon path={VEHICLES[driver.vehicle_details.type].icon} size={1.125} title='seat(s)' />
              </Box>
            }
            title={driver.name}
            subheader={`${driver.passengers.length + 1}/${driver.vehicle_details.availableSeats} ${
              driver.vehicle_details.availableSeats > 1 ? "seats" : "seat"
            }`}
          />
          <Divider />
          <CardContent>
            <Box display='flex' flexWrap='wrap' gap={2} justifyContent={noSeatsAvailable ? "center" : "flex-start"}>
              {passengers.map((passenger) => {
                const isOwningSeat = passenger.uid === userAuthId;

                return (
                  <SeatChip
                    avatarFile={passenger.avatar ? smallAvatars[passenger.avatar] : undefined}
                    avatarName={passenger.avatar}
                    key={passenger.uid}
                    color={isOwningSeat ? "primary" : undefined}
                    name={passenger.name ?? "-"}
                    onDelete={
                      !viewMode && (isOwnerOfCar || isOwningSeat)
                        ? () =>
                            handleDelete({
                              driverUid: driver.uid,
                              isOwnerOfCar,
                              isOwningSeat,
                              passengerId: passenger.uid,
                            })
                        : undefined
                    }
                  />
                );
              })}

              {emptySeats > 0 &&
                new Array(emptySeats)
                  .fill(0)
                  .map((_, index) => (
                    <EmptySeatChip
                      disabled={viewMode || isDriver || isPassenger}
                      key={index}
                      onClick={
                        isPassenger
                          ? undefined
                          : () => handleSelectEmptySeat(driver.uid, !isPassenger ? driverOfCurrentUser?.uid : undefined)
                      }
                    />
                  ))}
              {noSeatsAvailable ? (
                <Typography color='text.secondary' textAlign='center' variant='body2'>
                  No seats available
                </Typography>
              ) : null}
            </Box>
          </CardContent>
        </Card>
      );
    });

    return vrList;
  }

  return (
    <PageLayout appBar={<AppBar title='Available vehicles' onBack={onBack} />} paddingBottom={false}>
      {drivers.length > 0 && (
        <Box px={2} pt={2} mb={2}>
          <Typography alignItems='center' color='text.secondary' display='flex' variant='body2'>
            {getNounCount("vehicle", drivers.length)}, {passengersCount}/{getNounCount("seat", allAvailableSeats)}
          </Typography>
        </Box>
      )}

      {drivers.length ? (
        <VirtualizedList
          estimateSize={72}
          height={`calc(100vh - ${isMobileDevice ? "108px" : "148px"})`}
          style={{ listStyle: "none" }}
          rows={generateVirtualizedList()}
        />
      ) : (
        <DataIconPlaceholder text='Currently, no user is bringing their vehicle' icon={VEHICLES["mid-size-car"].icon} />
      )}
    </PageLayout>
  );
}

interface SeatChipProps {
  avatarFile?: Blob;
  avatarName: string | null;
  color?: ChipProps["color"];
  name: string;
  onDelete?: ChipProps["onDelete"];
}

function SeatChip(props: SeatChipProps) {
  const { avatarFile, avatarName, color, name, onDelete } = props;
  return (
    <Chip
      avatar={
        <AsyncAvatar
          avatarFile={avatarFile}
          avatarName={avatarName}
          placeholder={getNameInitials(name, 1)}
          bucket={StorageBuckets.PROFILES_AVATARS}
        />
      }
      color={color}
      label={name}
      variant='outlined'
      onDelete={onDelete}
    />
  );
}

interface EmptySeatChipProps {
  disabled?: boolean;
  onClick?: ChipProps["onClick"];
}

function EmptySeatChip(props: EmptySeatChipProps) {
  const { disabled, onClick } = props;

  return (
    <Chip
      role='button'
      aria-disabled={disabled ? "true" : "false"}
      disabled={disabled}
      onClick={onClick}
      variant='outlined'
      label='Empty'
      icon={<Icon path={mdiCarSeat} size={0.75} />}
    />
  );
}

interface HandleDeleteParams {
  driverUid: number;
  isOwningSeat: boolean;
  isOwnerOfCar: boolean;
  passengerId: number;
}

export default Vehicles;
